import React, { useEffect, useMemo, useState } from "react";
import "./Snailsforce.css";

const API_BASE = process.env.REACT_APP_SNAILSFORCE_API || "http://localhost:8787";

const STATUS_OPTIONS = ["All", "Lead", "Qualified", "Customer", "Dormant"];
const DEFAULT_FIELD_ORDER = [
  "full_name",
  "first_name",
  "last_name",
  "email",
  "phone",
  "company",
  "title",
  "status",
  "source",
  "city",
  "state",
  "country",
  "notes",
];

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Request failed (${response.status})`);
  }

  return payload;
}

function isServerOfflineMessage(message) {
  const value = String(message || "").toLowerCase();
  return (
    value.includes("failed to fetch") ||
    value.includes("fetch failed") ||
    value.includes("networkerror") ||
    value.includes("network error") ||
    value.includes("load failed") ||
    value.includes("econnrefused")
  );
}

function fmtDate(isoString) {
  if (!isoString) return "--";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString();
}

function fmtDateTime(isoString) {
  if (!isoString) return "--";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
}

function snailPercent(lead) {
  const days = Number(lead?.days_since_interaction || 0);
  return Math.max(0, Math.min(100, (days / 30) * 100));
}

function snailDaysRemaining(lead) {
  const days = Math.max(0, Number(lead?.days_since_interaction || 0));
  return Math.max(0, 30 - Math.floor(days));
}

function SnailProgress({ lead }) {
  const progress = snailPercent(lead);
  const daysRemaining = snailDaysRemaining(lead);

  return (
    <div className="snailsforce-snail-wrap" aria-label="30 day follow-up progress">
      <div className="snailsforce-snail-track" />
      <div
        className="snailsforce-snail-fill"
        style={{ width: `${progress}%` }}
      />
      <div
        className="snailsforce-snail-icon"
        style={{ left: `calc(${progress}% - 10px)` }}
        title={`Snail progress ${Math.round(progress)}%`}
      >
        🐌
      </div>
      <div className="snailsforce-sunflower" title="Follow-up deadline">
        🌻
      </div>
      <div className="snailsforce-snail-meta">
        {Math.round(progress)}% of 30-day window • {daysRemaining} day(s) left
      </div>
    </div>
  );
}

function KpiCard({ label, value, subtext }) {
  return (
    <div className="snailsforce-kpi-card">
      <p className="snailsforce-kpi-label">{label}</p>
      <h3 className="snailsforce-kpi-value">{value}</h3>
      <p className="snailsforce-kpi-subtext">{subtext}</p>
    </div>
  );
}

const Snailsforce = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [pruneResult, setPruneResult] = useState(null);
  const [kpis, setKpis] = useState({});
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isDraggingCsv, setIsDraggingCsv] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState("Lead");
  const [noteDraft, setNoteDraft] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  const fieldConfig = useMemo(() => {
    if (!importPreview?.fields?.length) {
      return DEFAULT_FIELD_ORDER.map((key) => ({ key, label: key.replace(/_/g, " ") }));
    }

    const indexed = {};
    importPreview.fields.forEach((field) => {
      indexed[field.key] = field;
    });

    return DEFAULT_FIELD_ORDER.map((key) => {
      if (indexed[key]) {
        return indexed[key];
      }

      return { key, label: key.replace(/_/g, " ") };
    });
  }, [importPreview]);

  const fetchKpis = async () => {
    const result = await request("/api/snailsforce/kpis");
    setKpis(result);
  };

  const fetchLeads = async (nextQuery = query, nextStatus = status) => {
    const params = new URLSearchParams();
    if (nextQuery) {
      params.set("query", nextQuery);
    }
    if (nextStatus && nextStatus !== "All") {
      params.set("status", nextStatus);
    }

    const result = await request(`/api/snailsforce/contacts?${params.toString()}`);
    setLeads(result);

    if (selectedLead) {
      const refreshed = result.find((lead) => lead.id === selectedLead.id);
      if (refreshed) {
        setSelectedLead(refreshed);
      } else {
        setSelectedLead(null);
      }
    }
  };

  const bootstrap = async () => {
    setIsLoading(true);
    setError("");

    try {
      const prune = await request("/api/snailsforce/prune", {
        method: "POST",
      });
      setPruneResult(prune);
      await Promise.all([fetchKpis(), fetchLeads("", "All")]);
      setStatus("All");
      setQuery("");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setNoteDraft(selectedLead?.notes || "");
  }, [selectedLead?.id, selectedLead?.notes]);

  const onSearch = async (event) => {
    event.preventDefault();
    setError("");

    try {
      await fetchLeads(query, status);
    } catch (err) {
      setError(err.message);
    }
  };

  const onStatusChange = async (nextStatus) => {
    setStatus(nextStatus);
    setError("");
    try {
      await fetchLeads(query, nextStatus);
    } catch (err) {
      setError(err.message);
    }
  };

  const onLeadClick = async (lead) => {
    setError("");
    try {
      const interacted = await request(`/api/snailsforce/contacts/${lead.id}/interact`, {
        method: "POST",
      });
      setSelectedLead(interacted);

      const refreshed = leads.map((entry) => (entry.id === interacted.id ? interacted : entry));
      setLeads(refreshed);
      await fetchKpis();
    } catch (err) {
      setError(err.message);
    }
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setImportPreview(null);
    setCsvFileName("");
    setCsvText("");
    setMapping({});
    setDefaultStatus("Lead");
    setError("");
  };

  const processCsvText = async (text, fileName) => {
    setCsvFileName(fileName || "uploaded.csv");
    setCsvText(text);
    setError("");

    try {
      const preview = await request("/api/snailsforce/import/preview", {
        method: "POST",
        body: JSON.stringify({ csvText: text }),
      });

      setImportPreview(preview);
      setMapping(preview.suggestions?.mapping || {});
    } catch (err) {
      setError(err.message);
      setImportPreview(null);
      setMapping({});
    }
  };

  const readFile = async (file) => {
    const text = await file.text();
    await processCsvText(text, file.name);
  };

  const onFileInputChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    await readFile(file);
  };

  const onDrop = async (event) => {
    event.preventDefault();
    setIsDraggingCsv(false);
    const file = event.dataTransfer.files?.[0];

    if (!file) {
      return;
    }

    await readFile(file);
  };

  const onImportSubmit = async () => {
    if (!csvText || !importPreview) {
      return;
    }

    setIsImporting(true);
    setError("");

    try {
      const result = await request("/api/snailsforce/import", {
        method: "POST",
        body: JSON.stringify({
          csvText,
          mapping,
          defaultStatus,
        }),
      });

      closeImport();
      await bootstrap();
      setPruneResult((previous) => ({
        ...(previous || {}),
        imported_count: result.inserted_count,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsImporting(false);
    }
  };

  const onSaveNote = async () => {
    if (!selectedLead || isSavingNote) {
      return;
    }

    setIsSavingNote(true);
    setError("");
    try {
      const updatedLead = await request(`/api/snailsforce/contacts/${selectedLead.id}/notes`, {
        method: "PUT",
        body: JSON.stringify({ notes: noteDraft }),
      });
      setSelectedLead(updatedLead);
      setLeads((previous) =>
        previous.map((lead) => (lead.id === updatedLead.id ? updatedLead : lead))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSavingNote(false);
    }
  };

  const staleSoonCount = leads.filter((lead) => snailPercent(lead) >= 66).length;
  const showOfflineHelp = isServerOfflineMessage(error);

  return (
    <div className="snailsforce-page">
      <header className="snailsforce-topbar">
        <div className="snailsforce-brand">
          <div className="snailsforce-logo">🐌</div>
          <div>
            <h1>Snailsforce</h1>
            <p>Slow and steady pipeline intelligence</p>
            <p className="snailsforce-brand-sub">Snail speed. Enterprise intent.</p>
          </div>
        </div>

        <div className="snailsforce-actions">
          <button className="snailsforce-btn snailsforce-btn-muted" onClick={bootstrap}>
            Reload + Prune
          </button>
          <button className="snailsforce-btn" onClick={() => setIsImportOpen(true)}>
            Import Leads CSV
          </button>
        </div>
      </header>

      {showOfflineHelp ? (
        <div className="snailsforce-error snailsforce-offline-help">
          <p className="snailsforce-offline-title">Snailsforce local API is not running.</p>
          <p>Start it in this repo, then reload this page:</p>
          <pre>
            <code>npm run snailsforce:server</code>
          </pre>
          <p className="snailsforce-offline-meta">Expected API base: {API_BASE}</p>
        </div>
      ) : null}
      {!showOfflineHelp && error ? <div className="snailsforce-error">{error}</div> : null}
      {!error && pruneResult?.deleted_count ? (
        <div className="snailsforce-notice">
          {pruneResult.deleted_count} contact(s) were deleted because their snail completed 30 days.
        </div>
      ) : null}
      {!error && pruneResult?.imported_count ? (
        <div className="snailsforce-notice">
          Imported {pruneResult.imported_count} contact(s) successfully.
        </div>
      ) : null}

      <section className="snailsforce-kpi-grid">
        <KpiCard
          label="Total Contacts"
          value={kpis.total_contacts || 0}
          subtext="All leads + customers"
        />
        <KpiCard
          label="New This Week"
          value={kpis.new_last_7_days || 0}
          subtext="Added in last 7 days"
        />
        <KpiCard
          label="Engaged (7d)"
          value={kpis.engaged_last_7_days || 0}
          subtext="Clicked/interacted recently"
        />
        <KpiCard
          label="Needs Check-In"
          value={kpis.needs_follow_up || staleSoonCount}
          subtext="20+ days since last interaction"
        />
      </section>

      <section className="snailsforce-main">
        <div className="snailsforce-list-panel">
          <form className="snailsforce-filters" onSubmit={onSearch}>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, company, notes..."
              className="snailsforce-search-input"
            />
            <select
              className="snailsforce-status-select"
              value={status}
              onChange={(event) => onStatusChange(event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button type="submit" className="snailsforce-btn">
              Filter
            </button>
          </form>

          <div className="snailsforce-list-header">
            <span>{leads.length} contact(s)</span>
            {isLoading ? <span>Loading...</span> : null}
          </div>

          <div className="snailsforce-list">
            {!isLoading && !leads.length ? (
              <div className="snailsforce-empty">
                No contacts yet. Import a CSV to start your snail pipeline.
              </div>
            ) : null}

            {leads.map((lead) => {
              const selected = selectedLead?.id === lead.id;
              return (
                <button
                  type="button"
                  key={lead.id}
                  className={`snailsforce-lead-card ${selected ? "snailsforce-lead-selected" : ""}`}
                  onClick={() => onLeadClick(lead)}
                >
                  <div className="snailsforce-lead-head">
                    <div>
                      <h3>{lead.full_name || "Unknown Contact"}</h3>
                      <p>{lead.title || "No title"}</p>
                    </div>
                    <span className="snailsforce-lead-status">{lead.status || "Lead"}</span>
                  </div>

                  <div className="snailsforce-lead-body">
                    <p>{lead.email || "No email"}</p>
                    <p>{lead.phone || "No phone"}</p>
                    <p>{lead.company || "No company"}</p>
                  </div>

                  <div className="snailsforce-lead-mini-meta">
                    <span>Interactions: {lead.interaction_count || 0}</span>
                    <span>Added: {fmtDate(lead.created_at)}</span>
                  </div>

                  <div className="snailsforce-lead-snail-zone">
                    <SnailProgress lead={lead} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="snailsforce-detail-panel">
          {!selectedLead ? (
            <div className="snailsforce-empty">Click a contact to inspect details and reset their snail.</div>
          ) : (
            <div>
              <h2>{selectedLead.full_name}</h2>
              <p className="snailsforce-detail-subtitle">{selectedLead.title || "No title set"}</p>

              <div className="snailsforce-detail-grid">
                <div>
                  <span>Email</span>
                  <p>{selectedLead.email || "--"}</p>
                </div>
                <div>
                  <span>Phone</span>
                  <p>{selectedLead.phone || "--"}</p>
                </div>
                <div>
                  <span>Company</span>
                  <p>{selectedLead.company || "--"}</p>
                </div>
                <div>
                  <span>Status</span>
                  <p>{selectedLead.status || "Lead"}</p>
                </div>
                <div>
                  <span>Source</span>
                  <p>{selectedLead.source || "--"}</p>
                </div>
                <div>
                  <span>Location</span>
                  <p>
                    {[selectedLead.city, selectedLead.state, selectedLead.country]
                      .filter(Boolean)
                      .join(", ") || "--"}
                  </p>
                </div>
                <div>
                  <span>Created</span>
                  <p>{fmtDate(selectedLead.created_at)}</p>
                </div>
                <div>
                  <span>Last Interaction</span>
                  <p>{fmtDateTime(selectedLead.last_interaction_at)}</p>
                </div>
                <div>
                  <span>Interaction Count</span>
                  <p>{selectedLead.interaction_count || 0}</p>
                </div>
              </div>

              <div className="snailsforce-detail-notes">
                <span>Notes</span>
                <textarea
                  className="snailsforce-notes-input"
                  placeholder="Write a note for this contact..."
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                />
                <button className="snailsforce-btn" disabled={isSavingNote} onClick={onSaveNote}>
                  {isSavingNote ? "Saving..." : "Save Note"}
                </button>
              </div>

              {selectedLead.raw_data && Object.keys(selectedLead.raw_data).length ? (
                <div className="snailsforce-detail-raw">
                  <h4>Imported Extra Fields</h4>
                  <div className="snailsforce-raw-table">
                    {Object.entries(selectedLead.raw_data).map(([key, value]) => (
                      <div key={key}>
                        <span>{key}</span>
                        <p>{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </aside>
      </section>

      {isImportOpen ? (
        <div className="snailsforce-modal-backdrop" onClick={closeImport}>
          <div className="snailsforce-modal" onClick={(event) => event.stopPropagation()}>
            <div className="snailsforce-modal-header">
              <h2>Import Leads CSV</h2>
              <button className="snailsforce-modal-close" onClick={closeImport}>
                Close
              </button>
            </div>

            <div
              className={`snailsforce-dropzone ${isDraggingCsv ? "snailsforce-dropzone-active" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingCsv(true);
              }}
              onDragLeave={() => setIsDraggingCsv(false)}
              onDrop={onDrop}
            >
              <p>Drag and drop CSV file here</p>
              <p>or</p>
              <label className="snailsforce-btn snailsforce-file-label">
                Choose CSV File
                <input type="file" accept=".csv,text/csv" onChange={onFileInputChange} />
              </label>
              {csvFileName ? <p className="snailsforce-file-name">Loaded: {csvFileName}</p> : null}
            </div>

            {importPreview ? (
              <div className="snailsforce-import-config">
                <p>
                  Parsed {importPreview.row_count} row(s). Review mapping and confirm import.
                </p>

                <div className="snailsforce-import-defaults">
                  <label>
                    Default status for unmapped rows
                    <select
                      value={defaultStatus}
                      onChange={(event) => setDefaultStatus(event.target.value)}
                    >
                      {STATUS_OPTIONS.filter((option) => option !== "All").map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="snailsforce-mapping-table">
                  {fieldConfig.map((field) => (
                    <div key={field.key} className="snailsforce-mapping-row">
                      <div>
                        <strong>{field.label}</strong>
                        <p>
                          Auto-confidence:{" "}
                          {Math.round((importPreview.suggestions?.scores?.[field.key] || 0) * 100)}%
                        </p>
                      </div>

                      <select
                        value={mapping[field.key] || ""}
                        onChange={(event) =>
                          setMapping((previous) => ({
                            ...previous,
                            [field.key]: event.target.value || null,
                          }))
                        }
                      >
                        <option value="">Ignore</option>
                        {importPreview.headers.map((header) => (
                          <option key={`${field.key}-${header}`} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <button className="snailsforce-btn" disabled={isImporting} onClick={onImportSubmit}>
                  {isImporting ? "Importing..." : "Import Contacts"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Snailsforce;
